import React from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, X, ChevronDown } from "lucide-react";
import { CaseStudyItem } from "@/types/case-study";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { cleanWavyUnderlines } from "@/utils/cleanWavyUnderlines";
import { Button } from "@/components/ui/button";
import { CoverImageCropModal } from "./CoverImageCropModal";

interface CaseStudyDetailsCardProps {
  caseStudy: CaseStudyItem;
  onUpdate: (updates: Partial<CaseStudyItem>) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  titleError: string;
  onTitleChange: (title: string) => void;
  onTitleBlur: (title: string) => void;
  coverImagePreview: string | null;
  isDraggingCover: boolean;
  isUploadingCover: boolean;
  coverImageError: string | null;
  coverImageInputRef: React.RefObject<HTMLInputElement>;
  onCoverImageClick: () => void;
  onCoverImageDragOver: (e: React.DragEvent) => void;
  onCoverImageDragLeave: (e: React.DragEvent) => void;
  onCoverImageDrop: (e: React.DragEvent) => void;
  onRemoveCoverImage: () => void;
  onCoverImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showCropModal: boolean;
  cropModalImageSrc: string;
  onCropComplete: (croppedBlob: Blob) => void;
  onCropCancel: () => void;
  onToggleCollapseAll?: () => void;
  areAllCollapsed?: boolean;
  isActive?: boolean;
  onActivate?: () => void;
}

export const CaseStudyDetailsCard: React.FC<CaseStudyDetailsCardProps> = ({
  caseStudy,
  onUpdate,
  isCollapsed,
  onToggleCollapse,
  titleError,
  onTitleChange,
  onTitleBlur,
  coverImagePreview,
  isDraggingCover,
  isUploadingCover,
  coverImageError,
  coverImageInputRef,
  onCoverImageClick,
  onCoverImageDragOver,
  onCoverImageDragLeave,
  onCoverImageDrop,
  onRemoveCoverImage,
  onCoverImageUpload,
  showCropModal,
  cropModalImageSrc,
  onCropComplete,
  onCropCancel,
  onToggleCollapseAll,
  areAllCollapsed = false,
  isActive = false,
  onActivate,
}) => {
  const handleActivate = () => {
    onActivate?.();
  };

  return (
    <Card
      className={`mb-6 group transition-all ${
        isActive ? 'ring-2 ring-orange-500 shadow-lg' : 'hover:shadow-md'
      }`}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        handleActivate();
      }}
    >
      <CardHeader
        className="hover:bg-gray-50/50 transition-colors cursor-pointer"
        onClick={onToggleCollapse}
        onMouseDown={(e) => {
          e.stopPropagation();
          handleActivate();
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex justify-between items-center w-full">
            <CardTitle className="text-base">Case Study Details</CardTitle>
            {onToggleCollapseAll && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCollapseAll();
                }}
                className="h-9 px-4 rounded-md border border-gray-300 hover:border-gray-400 hover:bg-white hover:text-black"
              >
                <span className="text-sm">{areAllCollapsed ? 'Expand All' : 'Collapse All'}</span>
              </Button>
            )}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleActivate();
              onToggleCollapse();
            }}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronDown
              className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
                isCollapsed ? '' : 'rotate-180'
              }`}
            />
          </button>
        </div>
      </CardHeader>
      {!isCollapsed && (
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Title</label>
            <Input
              value={caseStudy.title}
              onChange={(e) => onTitleChange(e.target.value)}
              onBlur={() => onTitleBlur(caseStudy.title)}
              placeholder="Enter case study title"
              className={titleError ? "border-red-500 focus:border-red-500" : ""}
              onFocus={handleActivate}
            />
            {titleError && (
              <p className="text-red-500 text-sm mt-1">{titleError}</p>
            )}
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Description</label>
            <RichTextEditor
              value={caseStudy.description || ""}
              onChange={(value) => {
                const cleanedValue = cleanWavyUnderlines(value);
                onUpdate({ description: cleanedValue });
              }}
              placeholder="Brief description of your case study"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Cover Image</label>
            {coverImagePreview ? (
              <div className="relative group">
                <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={coverImagePreview}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={onRemoveCoverImage}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
                {isUploadingCover && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-white text-sm">Uploading...</div>
                  </div>
                )}
              </div>
            ) : (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                  isDraggingCover
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-300 bg-gray-50/50 hover:border-gray-400'
                } ${isUploadingCover ? 'opacity-50 pointer-events-none' : ''}`}
                onClick={onCoverImageClick}
                onDragOver={onCoverImageDragOver}
                onDragLeave={onCoverImageDragLeave}
                onDrop={onCoverImageDrop}
                onMouseDown={handleActivate}
              >
                <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-700">Upload cover image</p>
                <p className="text-xs text-gray-500">Click to browse or drag here</p>
                {coverImageError && (
                  <p className="text-xs text-red-500 mt-2">{coverImageError}</p>
                )}
              </div>
            )}
            <input
              ref={coverImageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onCoverImageUpload}
              disabled={isUploadingCover}
            />
          </div>
        </CardContent>
      )}

      {/* Cover Image Crop Modal */}
      {showCropModal && cropModalImageSrc && (
        <CoverImageCropModal
          open={showCropModal}
          imageSrc={cropModalImageSrc}
          onCropComplete={onCropComplete}
          onCancel={onCropCancel}
        />
      )}
    </Card>
  );
};
